import { Crown, Building2 } from "lucide-react";
import { EVENT_CONFIG, fmtUSD } from "@/config/eventConfig";
import { SectionHeading, Reveal, Thermometer, CountUp, GoldButton, useDonate } from "./shared";

const Campaign = () => {
  const { openDonate } = useDonate();
  const { fundraising: f, org } = EVENT_CONFIG;
  const remaining = Math.max(0, f.goal - f.raised);

  const stats = [
    { label: "Currently Raised", value: f.raised, prefix: "$", testid: "fundraising-thermometer-total" },
    { label: "Remaining", value: remaining, prefix: "$", testid: "fundraising-remaining" },
    { label: "Donors", value: f.donors, prefix: "", testid: "fundraising-donors" },
    { label: "Children Impacted", value: f.childrenImpacted, prefix: "", testid: "fundraising-children" },
  ];

  return (
    <section id="campaign" data-testid="campaign-section" className="relative py-24 sm:py-32 bg-gradient-to-b from-ink via-wine/20 to-ink grain overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-14">
        <SectionHeading
          chapter="01"
          eyebrow={EVENT_CONFIG.event.campaign}
          title={
            <>
              THE GOAL: <span className="gold-shimmer">$2,000,000</span>
            </>
          }
          sub="Help us build crowns for the future — every gift moves a child closer to confidence, care and belonging."
        />

        <Reveal className="w-full max-w-4xl pb-8">
          <Thermometer goal={f.goal} raised={f.raised} milestones={f.milestones} testidPrefix="fundraising-thermometer" />
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 w-full max-w-5xl">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div data-testid={s.testid} className="gold-stroke rounded-2xl bg-coal/70 p-5 sm:p-7 text-center">
                <div className="font-serif text-2xl sm:text-3xl gold-text">
                  <CountUp value={s.value} prefix={s.prefix} />
                </div>
                <div className="mt-2 text-[10px] sm:text-xs uppercase tracking-[0.22em] text-cream/70">
                  {s.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="w-full max-w-4xl">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-gold mb-5">Build a crown tonight</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
            {EVENT_CONFIG.donationAmounts.map((a) => (
              <button
                key={a}
                data-testid={`campaign-donate-${a}`}
                onClick={() => openDonate(a)}
                className="py-3.5 rounded-xl border border-champagne/25 text-champagne text-sm font-semibold tracking-wide hover:bg-champagne hover:text-ink transition-colors duration-200"
              >
                {a >= 100000 ? "$100K+" : fmtUSD(a)}
              </button>
            ))}
            <button
              data-testid="campaign-donate-other"
              onClick={() => openDonate()}
              className="py-3.5 rounded-xl bg-wine border border-winelight text-ivory text-sm font-semibold tracking-wide hover:bg-winelight transition-colors duration-200"
            >
              Other
            </button>
            <button
              data-testid="campaign-donate-monthly"
              onClick={() => openDonate(25)}
              className="py-3.5 rounded-xl border border-dashed border-champagne/40 text-cream text-sm font-semibold tracking-wide hover:text-champagne hover:border-champagne transition-colors duration-200"
            >
              Monthly
            </button>
          </div>
        </Reveal>

        <Reveal className="flex flex-col sm:flex-row gap-4 mt-2">
          <GoldButton
            testid="campaign-transformative-gift-button"
            variant="wine"
            href={`mailto:${org.email}?subject=Transformative%20Gift%20—%20Building%20Crowns%204%20The%20Future`}
          >
            <Crown size={15} /> Make a Transformative Gift
          </GoldButton>
          <GoldButton testid="campaign-crown-partner-button" variant="outline" href="#sponsor">
            <Building2 size={15} /> Become a Crown Partner
          </GoldButton>
        </Reveal>
      </div>
    </section>
  );
};

export default Campaign;
